import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { StyledValueDetails } from '../styles';
import { useLicenceAuthorities } from '../hooks';
import { Loader } from 'components/atoms/Loader';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { formatSendHour, getSendFrequencyLabel } from '../helpers';
import { EIconTypes } from '../../../constants/Icons';
import { CopyableTruncatedField } from '../../../components/molecules/CopyableTruncatedField';
import { FormModal } from '../../../components/molecules/Modal';

enum EPrintoutSettingsModalTypes {
  PGP = 'PGP',
  SSH = 'SSH',
}

const LicenceAuthoritiesPrintoutSettingsPage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { samePageAlert, nextPageAlert, clearAlert } = useAlert();
  const { id = '' } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isRotatingPGP, setIsRotatingPGP] = useState(false);
  const [isRotatingSSH, setIsRotatingSSH] = useState(false);
  const [rotateModalType, setRotateModalType] = useState<EPrintoutSettingsModalTypes | null>(
    null,
  );

  const {
    printoutSettings,
    getPrintoutSettings,
    rotatePGPKeyPrintoutSettings,
    rotateSSHKeyPrintoutSettings,
  } = useLicenceAuthorities();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      await getPrintoutSettings(id);
      setIsLoading(false);
    })();
  }, [id]);

  const sendHours = printoutSettings?.sendHours ?? [];

  const handleEdit = () =>
    navigate(ERouteLinks.EditLicenceAuthoritiesPrintoutSettings.replace(':id', id));
  const handleDelete = () =>
    navigate(ERouteLinks.DeleteLicenceAuthoritiesPrintoutSettings.replace(':id', id));

  const handleConfirmRotatePGPKey = async () => {
    if (!printoutSettings) return;
    setIsRotatingPGP(true);
    try {
      const success = await rotatePGPKeyPrintoutSettings(id, printoutSettings.version);
      if (success) {
        await getPrintoutSettings(id);
      }
    } finally {
      setIsRotatingPGP(false);
      setRotateModalType(null);
    }
  };

  const handleConfirmRotateSSHKey = async () => {
    if (!printoutSettings) return;
    setIsRotatingSSH(true);
    try {
      const success = await rotateSSHKeyPrintoutSettings(id, printoutSettings.version);
      if (success) {
        await getPrintoutSettings(id);
      }
    } finally {
      setIsRotatingSSH(false);
      setRotateModalType(null);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          { label: t('licenceAuthorities.header'), route: ERouteLinks.LicenceAuthorities },
          {
            label: t('licenceAuthorities.detailsLicenceAuthority.header'),
            route: ERouteLinks.DetailsLicenceAuthorities.replace(':id', id),
          },
          t('licenceAuthorities.printoutSettingsDetails.header'),
        ]}
      />
      <StyledPageTitle variant="h4">
        <Grid
          container
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            mb: 1,
            mt: 1,
            marginLeft: '-5px',
          }}
          columnSpacing="10px"
        >
          {t('licenceAuthorities.printoutSettingsDetails.header')}
          <Grid container sx={{ display: 'flex', flexWrap: 'nowrap' }} columnSpacing="10px">
            <Button
              label={t('general.edit.button')}
              variant={EButtonVariants.contained}
              onClick={handleEdit}
            />
            <Button
              label={t('general.delete.text')}
              variant={EButtonVariants.contained}
              onClick={handleDelete}
            />
          </Grid>
        </Grid>
      </StyledPageTitle>

      {samePageAlert && (
        <Grid size={{ xs: 12 }}>
          <Box sx={{ width: '100%', my: 2 }}>
            <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
          </Box>
        </Grid>
      )}
      {nextPageAlert && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}
      {isLoading ? (
        <Loader isLoading={true} />
      ) : (
        <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.username.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.username)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.host.label')}
            </Typography>
            <Typography variant="body2">{emptyFieldHolder(printoutSettings?.host)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.port.label')}
            </Typography>
            <Typography variant="body2">{emptyFieldHolder(printoutSettings?.port)}</Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.fingerprints.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.hostFingerprints?.join(', '))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.outboundPath.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.outboundPath)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.inboudPath.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.inboundPath)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <CopyableTruncatedField
              label={t('licenceAuthoritiesPrintoutSettings.keyPrintoutSystem.label')}
              value={emptyFieldHolder(printoutSettings?.pgpForeignPublicKey)}
              maxLength={50}
              buttonIcon={EIconTypes.copy}
              buttonVariant={EButtonVariants.outlined}
              buttonSize={EButtonSizes.small}
              buttonSizePX={EButtonSizesPX.px40}
            />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <CopyableTruncatedField
                  label={t('licenceAuthoritiesPrintoutSettings.keyFirecycleSystem.label')}
                  value={emptyFieldHolder(printoutSettings?.pgpOwnPublicKey)}
                  maxLength={50}
                  buttonIcon={EIconTypes.copy}
                  buttonVariant={EButtonVariants.outlined}
                  buttonSize={EButtonSizes.small}
                  buttonSizePX={EButtonSizesPX.px40}
                />
              </Box>
              <Box sx={{ mt: 3 }}>
                <Button
                  label={t('licenceAuthoritiesPrintoutSettings.rotatePGPKey.button')}
                  variant={EButtonVariants.outlined}
                  onClick={() => setRotateModalType(EPrintoutSettingsModalTypes.PGP)}
                  size={EButtonSizes.small}
                  sizePX={EButtonSizesPX.px40}
                  disabled={isRotatingPGP || !printoutSettings?.pgpOwnPublicKey}
                />
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <CopyableTruncatedField
                  label={t('licenceAuthoritiesPrintoutSettings.publicKey.label')}
                  value={emptyFieldHolder(printoutSettings?.sshOwnPublicKey)}
                  maxLength={50}
                  buttonIcon={EIconTypes.copy}
                  buttonVariant={EButtonVariants.outlined}
                  buttonSize={EButtonSizes.small}
                  buttonSizePX={EButtonSizesPX.px40}
                />
              </Box>
              <Box sx={{ mt: 3 }}>
                <Button
                  label={t('licenceAuthoritiesPrintoutSettings.rotateSSHKey.button')}
                  variant={EButtonVariants.outlined}
                  onClick={() => setRotateModalType(EPrintoutSettingsModalTypes.SSH)}
                  size={EButtonSizes.small}
                  sizePX={EButtonSizesPX.px40}
                  disabled={isRotatingSSH || !printoutSettings?.sshOwnPublicKey}
                />
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.sendFrequency.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(getSendFrequencyLabel(printoutSettings?.sendHours, t))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.firstPrintingHours.label')}
            </Typography>

            <Typography variant="body2">
              {emptyFieldHolder(formatSendHour(sendHours[0]))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.secondPrintingHours.label')}
            </Typography>

            <Typography variant="body2">
              {emptyFieldHolder(formatSendHour(sendHours[1]))}
            </Typography>
          </Grid>
        </StyledValueDetails>
      )}

      <FormModal
        open={rotateModalType === EPrintoutSettingsModalTypes.PGP}
        onClickClose={() => setRotateModalType(null)}
        onClickApprove={handleConfirmRotatePGPKey}
        title={t('licenceAuthoritiesPrintoutSettings.rotatePGPKeyModal.title.text')}
        text={t('licenceAuthoritiesPrintoutSettings.rotatePGPKeyModal.info.text')}
        approveButtonLabel={t('general.yes.button')}
        cancelButtonLabel={t('general.no.button')}
      />

      <FormModal
        open={rotateModalType === EPrintoutSettingsModalTypes.SSH}
        onClickClose={() => setRotateModalType(null)}
        onClickApprove={handleConfirmRotateSSHKey}
        title={t('licenceAuthoritiesPrintoutSettings.rotateSSHKeyModal.title.text')}
        text={t('licenceAuthoritiesPrintoutSettings.rotateSSHKeyModal.info.text')}
        approveButtonLabel={t('general.yes.button')}
        cancelButtonLabel={t('general.no.button')}
      />
    </>
  );
};

export default LicenceAuthoritiesPrintoutSettingsPage;
